import { observable } from "@legendapp/state";
import { mergeColumnsConfigs } from "@repo/shared/utils/merge-columns-configs";
import type * as Ably from "ably";

import { syncedCrudPersist } from "~/libs/legend-state";
import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { appDataReady$ } from "../app-data-ready";
import { reconcileBoardRefs } from "../board-payloads/board-payloads.actions";
import { isSnapshotOrigin } from "../board-snapshot/snapshot-origins";
import { datasets$ } from "../datasets";
import {
  getLocalUploadBlocks,
  isLocalUploadBlock,
  isLocalUploadDataset,
  mergeLocalUploadBlock,
  removeLocalUploadBlock,
  saveLocalUploadBlock,
} from "../datasets/local-uploads";
import { cleanupOrphanedBoardFilterColumns } from "./blocks.actions";
import { sanitizeStatConfig } from "./blocks.stat.sanitize";
import type { Block } from "./blocks.types";

export const blocks$ = observable(
  syncedCrudPersist<Block>({
    persist: {
      name: "blocks",
    },
    waitFor: appDataReady$,
    list: async () => {
      const blocks = await trpc.blocks.list.query();
      // `mode: "set"` replaces the store on every refresh, so client-only
      // blocks have to be re-merged from their registry each time.
      return [...blocks, ...getLocalUploadBlocks()];
    },
    update: async ({ id, ...input }) => {
      if (!id) return;
      if (isLocalUploadBlock(id)) {
        mergeLocalUploadBlock(id, input);
        return;
      }
      // An existing server block pointed at a client-only upload (the "add
      // block, then pick a dataset" flow). It can't keep its row — the
      // `datasetId` foreign key would reject it — so drop the row and adopt
      // the block locally instead.
      if (isLocalUploadDataset(input.datasetId)) {
        const block = blocks$[id].peek();
        if (block) saveLocalUploadBlock(block);
        await trpc.blocks.delete.mutate({ blockId: id }).catch(() => undefined);
        return;
      }
      if (isSnapshotOrigin(id)) return;

      // Defensive guard: a debounced partial sync can momentarily carry a
      // `statConfig` whose items are missing `columnId`/`operation`, which the
      // server's `StatItemSchema` rejects. Strip those before they reach it.
      const sanitized = input.statConfig
        ? { ...input, statConfig: sanitizeStatConfig(input.statConfig) }
        : input;

      return await trpc.blocks.update.mutate({
        id,
        ...sanitized,
      });
    },
    create: async (input) => {
      // `blocks.datasetId` is a foreign key, so a block pointing at a
      // client-only upload can never exist server-side — keep it local instead
      // of letting the insert fail.
      if (isLocalUploadDataset(input.datasetId)) {
        saveLocalUploadBlock(input);
        return input;
      }
      if (isSnapshotOrigin(input.id)) return input;
      const sanitized = input.statConfig
        ? { ...input, statConfig: sanitizeStatConfig(input.statConfig) }
        : input;
      return await trpc.blocks.create.mutate(sanitized);
    },
    delete: async (input) => {
      if (!input.id) return;
      if (isLocalUploadBlock(input.id)) {
        removeLocalUploadBlock(input.id);
        return;
      }
      if (isSnapshotOrigin(input.id)) return;

      await trpc.blocks.delete.mutate({
        blockId: input.id,
      });
    },
    subscribe: ({ update }) => {
      const channel = getOrgChannel();
      if (!channel) return;

      const handler = (message: Ably.Message) => {
        const items = Array.isArray(message.data)
          ? message.data
          : [message.data];
        const filtered = items.filter((item) => !isSnapshotOrigin(item?.id));
        if (filtered.length === 0) return;
        update({
          mode: "merge",
          value: filtered,
        });
      };

      channel.subscribe("blocks", handler);

      return () => {
        channel.unsubscribe("blocks", handler);
      };
    },
    transform: {
      // Self-heal persisted columnsConfig on load: merge defaults from the
      // dataset and drop orphan keys that no longer exist in the dataset's
      // columns. Rift ids and stats are derived reactively by consumers —
      // see `useBlockRift` / `useRiftStat`.
      load: (block) => {
        const { datasetId, columnsConfig } = block;
        if (!datasetId) return block;

        const datasetColumnsConfig = datasets$[datasetId].columnsConfig.get();
        const runId = datasets$[datasetId].runId.get();
        const datasetColumns = runId
          ? datasets$[datasetId].columns.get()
          : null;

        block.columnsConfig = mergeColumnsConfigs(
          datasetColumnsConfig,
          columnsConfig,
        );

        // Only prune once the dataset has published columns (a `runId`
        // is set) so we don't wipe valid config while the dataset is loading.
        if (datasetColumns) {
          const cleaned: typeof block.columnsConfig = {};
          for (const [name, cfg] of Object.entries(block.columnsConfig)) {
            if (name in datasetColumns) cleaned[name] = cfg;
          }
          block.columnsConfig = cleaned;
        }

        return block;
      },
    },
  }),
);

blocks$.onChange(({ changes }) => {
  const boardsToCleanup = new Set<string>();
  const boardsToReconcile = new Set<string>();

  for (const change of changes) {
    // Bulk hydrate of the entire blocks dictionary — reconcile every board
    // referenced by any block in the new payload so a board that mounted
    // before blocks were hydrated picks up its dataset refs.
    if (change.path.length === 0 && change.valueAtPath) {
      const all = change.valueAtPath as Record<string, Block | undefined>;
      for (const block of Object.values(all)) {
        if (block?.boardId) boardsToReconcile.add(block.boardId);
      }
      continue;
    }

    const blockId = change.path[0] as string;

    // datasetId changed — reconcile the board's payload refs so the new
    // dataset starts loading and the abandoned one releases its ref.
    if (change.path[1] === "datasetId") {
      const boardId = blocks$[blockId].boardId.get();
      if (boardId) {
        boardsToCleanup.add(boardId);
        boardsToReconcile.add(boardId);
      }
    }

    // Whole-block insert/delete — reconcile the affected board's refs.
    if (change.path.length === 1) {
      const blockBoardId =
        (change.valueAtPath as { boardId?: string } | undefined)?.boardId ??
        (change.prevAtPath as { boardId?: string } | undefined)?.boardId;
      if (blockBoardId) boardsToReconcile.add(blockBoardId);
    }

    // boardId changed — cleanup orphaned filters on the old board and
    // reconcile both old and new boards.
    if (change.path[1] === "boardId" && change.prevAtPath) {
      boardsToCleanup.add(change.prevAtPath as string);
      boardsToReconcile.add(change.prevAtPath as string);
      const newBoardId = change.valueAtPath as string | undefined;
      if (newBoardId) boardsToReconcile.add(newBoardId);
    }
  }

  for (const boardId of boardsToCleanup) {
    cleanupOrphanedBoardFilterColumns(boardId);
  }
  for (const boardId of boardsToReconcile) {
    reconcileBoardRefs(boardId);
  }
});
